<template>
    <div class="learning-admin-element-list" data-test-view="learning-admin-element">
        <l-headline
            :title="$t(`learning.admin.element.title`)"
            :sub-title="$t(`learning.admin.element.subTitle`)"
            :tabs="tabs"
        ></l-headline>

        <div v-show="hasElements">
            <div class="row filters">
                <div :class="[`col-md-${12 - 2 * filters.length}`]">
                    <l-input-text
                        v-model="query"
                        type="text"
                        :optional="true"
                        :label="''"
                        :placeholder="$t(`learning.admin.element.search.placeholder`)"
                        data-test-id="learning-admin-element-search"
                    />
                </div>
                <div v-for="filter in filters" :key="filter.key" class="col-md-2">
                    <l-select
                        :data-test-id="`learning-admin-element-filter-${filter.key}`"
                        :options="
                            filter.options.map(option => ({
                                value: option.value,
                                text:
                                    option.text ||
                                    $t(
                                        `learning.admin.element.filters.${filter.key}.${option.label}`,
                                    ),
                            }))
                        "
                        :value="filter.value"
                        @change="
                            e => {
                                filter.value = e.target.value;

                                filterBy(filter.key, filter.value);
                            }
                        "
                    />
                </div>
            </div>

            <hr />
        </div>

        <l-paginate
            :list="filteredList"
            :items-per-page="10"
            :on-item-click="goToDetailsElement"
            :no-data-text="
                hasElements
                    ? $t(`learning.admin.element.noResults`)
                    : $t(`learning.admin.element.empty`)
            "
            :no-data-btn-show="rights.L_ELEMENT"
            :no-data-btn-text="$t(`learning.admin.element.btn.createEmpty`)"
            :no-data-btn-handler="createNewElement"
        >
            <template #item="{ item }">
                <l-list-item
                    class="clickable element-list-item"
                    data-test-class="element-list-item"
                >
                    <template #title>
                        <span class="element-name ellipsis" data-test-id="element-name">{{
                            item.name
                        }}</span>
                    </template>
                    <template #subtitle>
                        <div v-if="item.description" class="element-description ellipsis">
                            <i class="fal fa-comment fa-fw tw-mr-2"></i>
                            <span data-test-id="element-description">
                                <l-text :text="item.description" one-line />
                            </span>
                        </div>
                        <div class="element-paths ellipsis tw-space-x-3">
                            <span v-if="item.public" data-test-class="element-recommended">
                                <i class="fa fa-star fa-fw tw-mr-2"></i
                                >{{ $t(`learning.admin.element.details.label.recommended`) }}
                            </span>
                            <span v-if="item.usedInPaths && item.usedInPaths.length">
                                <i class="fa fa-puzzle-piece fa-fw tw-mr-2"></i
                                >{{ $t(`learning.admin.element.details.label.paths`) }}
                                {{
                                    item.usedInPaths
                                        .filter(path => path.latest)
                                        .reduce((a, c) => a + c.name + ", ", "")
                                        .slice(0, -2)
                                }}
                            </span>
                            <span v-else>
                                <i class="fa fa-puzzle-piece fa-fw tw-mr-2"></i
                                >{{ $t(`learning.admin.element.details.label.pathsEmpty`) }}
                            </span>
                        </div>
                    </template>
                    <template #left>
                        <div class="element-list-item-icon">
                            <img
                                class="element-list-item-icon-img"
                                :src="icons[item.type]"
                                :alt="item.type"
                            />
                        </div>
                    </template>
                </l-list-item>
            </template>
        </l-paginate>

        <l-modal v-show="showModal" size="l" :close-on-backdrop-click="false" @close="goBack">
            <template #header>{{ modalTitle }}</template>
            <template #body>
                <router-view></router-view>
            </template>
        </l-modal>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import LHeadline from "@/components/LHeadline";
import LInputText from "@/components/LInputText";
import LListItem from "@/components/LListItem";
import LModal from "@/components/LModal";
import LPaginate from "@/components/LPaginate";
import LSelect from "@/components/LSelect";
import LText from "@/components/LText";

import article from "@/img/icons/badge_book.png";
import quiz from "@/img/icons/badge_planchette.png";
import task from "@/img/icons/badge_puzzle.png";
import video from "@/img/icons/badge_projector.png";

export default {
    components: {
        LHeadline,
        LInputText,
        LListItem,
        LModal,
        LPaginate,
        LSelect,
        LText,
    },
    data() {
        return {
            showModal: false,
            modalTitle: "",
            query: "",
        };
    },
    computed: {
        ...mapGetters({
            rights: "users/current/rights",
            search: "learning/element/search",
            filters: "learning/element/filters",
            list: "learning/element/filteredList",
            hasElements: "learning/element/hasElements",
        }),
        tabs() {
            return [
                {
                    tabClass: "space",
                    show: true,
                },
                {
                    show: this.rights.L_ELEMENT,
                    label: this.$t(`learning.admin.element.btn.create`),
                    handler: this.createNewElement,
                    testId: "learning-admin-element-create",
                    tabClass: "btn-primary",
                    isButton: true,
                },
            ];
        },
    },
    watch: {
        $route: {
            immediate: true,
            handler(newRoute) {
                this.showModal = newRoute.meta && newRoute.meta.modal;
                this.modalTitle = this.getModalTitleFromRoute(newRoute);
            },
        },
        list: {
            immediate: true,
            handler() {
                this.filteredList = this.search(this.query);
            },
        },
        query: {
            immediate: true,
            handler(newQuery) {
                if (newQuery.length) {
                    this.filteredList = this.search(newQuery);
                } else {
                    this.filteredList = this.list;
                }
            },
        },
    },
    created() {
        this.icons = {
            article,
            quiz,
            task,
            video,
        };
    },
    methods: {
        createNewElement() {
            this.$router.push(`/learning/admin/element/new`);
        },
        goToEditElement({ commonId, type }) {
            this.$router.push(`/learning/admin/element/${type}/${commonId}/edit`);
        },
        goToDetailsElement({ commonId, type }) {
            this.$router.push(`/learning/admin/element/${type}/${commonId}/details`);
        },
        goBack() {
            this.$router.push(`/learning/admin/element`);
        },
        filterBy(key, value) {
            this.$store.dispatch({ type: "learning/element/filterBy", payload: { key, value } });
        },
        getModalTitleFromRoute(route) {
            switch (route.name) {
                case "LearningAdminElementNew":
                    return this.$t("learning.admin.element.details.title.new");
                case "LearningAdminElementDetails": {
                    const element = this.list.find(e => e.commonId === route.params.elementId);
                    return element?.name
                        ? this.$t("learning.admin.element.details.title.details", {
                              name: element.name,
                          })
                        : this.$t(`learning.admin.element.details.title.default`);
                }
                case "LearningAdminElementEdit": {
                    const element = this.list.find(e => e.commonId === route.params.elementId);
                    return element?.name
                        ? this.$t("learning.admin.element.details.title.edit", {
                              name: element.name,
                          })
                        : this.$t(`learning.admin.element.details.title.default`);
                }
                default:
                    return this.$t(`learning.admin.element.details.title.default`);
            }
        },
    },
};
</script>

<style scoped>
.dropdown {
    margin-top: 0px;
}

.filters {
    margin-bottom: 20px;
}
.filters >>> .form-group {
    padding-bottom: 7px;
}

.learning-admin-element-list >>> .list-item-right {
    width: calc(100% - 110px);
}
.element-list-item-icon {
    width: 110px;
    text-align: center;
}

.element-list-item-icon-img {
    width: 80px;
    opacity: 0.8;
    transition: all 0.3s ease-in;
}
.element-list-item:hover .element-list-item-icon-img {
    transform: scale(1.05);
    opacity: 1;
}
</style>
